import { createApp } from 'vue'

import pinia from '@/store/index'

import App from './App.vue'
import router from './router'

import './style.scss'

import * as Sentry from '@sentry/vue'
import { vOnClickOutside } from '@vueuse/components'
import VueSafeTeleport from 'vue-safe-teleport'

/*
Importing whole echarts to fix code splitting issue.
They encourage to import one by one for each use case instead of this mass import.
https://github.com/ecomfe/vue-echarts#readme
*/
import 'echarts'

import VueGoogleMaps from 'vue-google-maps-community-fork'

import {
  ConsoleTracker,
  MultiTracker,
  PosthogTracker,
  trackerPlugin
} from '@last/core-ui/paprika'
import { LDialogPlugin } from '@last/core-ui/paprika/plugins/dialog'
import eventSender from '@last/core-ui/scripts/eventSender'
import LNotificationPlugin from '@last/core-ui/v3/plugins/notification'

import { useAuthStore } from '@/store/auth'

import { createFilters } from './filters'
import i18n from './i18n'

const filters = createFilters(pinia)

const app = createApp({ name: 'LastAdmin', ...App })

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      new Sentry.BrowserTracing({
        routingInstrumentation: Sentry.vueRouterInstrumentation(router)
      }),
      new Sentry.Replay()
    ],
    tracesSampleRate: 1.0
  })
}

app.use(LDialogPlugin)
app.use(LNotificationPlugin)
app.use(pinia)
app.use(i18n)
app.use(router)
app.use(VueGoogleMaps, {
  load: {
    key: import.meta.env.VITE_GOOGLE_KEY,
    libraries: 'places,drawing,visualization'
  },
  installComponents: true
})
app.use(VueSafeTeleport)

app.config.globalProperties.$filters = filters
app.provide('filters', filters)
app.provide('$lnotification', app.config.globalProperties.$lnotification)
app.provide('$t', app.config.globalProperties.$t)
app.directive('click-outside', vOnClickOutside)

app.mount('#app')

const {
  VITE_GOOGLE_ANALYTICS_ID,
  VITE_FACEBOOK_ANALYTICS_ID,
  VITE_META_CONVERSION_API_TOKEN
} = import.meta.env

eventSender.load(
  {
    googleAnalyticsId: VITE_GOOGLE_ANALYTICS_ID,
    facebookAnalyticsId: VITE_FACEBOOK_ANALYTICS_ID,
    metaConversionApiToken: VITE_META_CONVERSION_API_TOKEN,
    domains: ['last.app', 'admin.last.app']
  },
  router,
  () => {
    const authStore = useAuthStore()
    return authStore.rootUser
  }
)

const trackers = []

if (import.meta.env.VITE_POSTHOG_KEY) {
  trackers.push(new PosthogTracker(import.meta.env.VITE_POSTHOG_KEY))
} else {
  trackers.push(new ConsoleTracker())
}

app.use(trackerPlugin, {
  tracker: new MultiTracker(trackers)
})
